import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { CrmInjectionToken, FieldValueInterface } from '../../../tokens-and-interfaces';
import { FieldMask, Pipeline as ProtoPipeline, PipelinesApiService } from '@vendasta/crm';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { convertGalaxyFilters, convertToGalaxyFilter } from '../../../utils';
import { StandardIds } from '../field.service';
import { HttpResponse } from '@angular/common/http';

export interface Pipeline {
  id: string;
  name: string;
  stages: Stage[];
}

export interface Stage {
  id: string;
  name: string;
  filters: GalaxyFilterInterface[];
  mutations: FieldValueInterface[];
}

function compareByProbability(a: Stage, b: Stage) {
  const aProb = a?.mutations?.find((mutation) => mutation?.fieldId === StandardIds.OpportunityProbability);
  const bProb = b?.mutations?.find((mutation) => mutation?.fieldId === StandardIds.OpportunityProbability);
  return aProb?.floatValue && bProb?.floatValue ? aProb.floatValue - bProb.floatValue : 0;
}

@Injectable({ providedIn: 'root' })
export class CrmPipelineService {
  constructor(
    private readonly pipelineService: PipelinesApiService,
    private injector: Injector,
  ) {}

  private get namespace$() {
    return this.injector.get(CrmInjectionToken).namespace$;
  }

  listPipelines$(objectType: string): Observable<Pipeline[]> {
    return this.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.pipelineService.listPipelines({
          listOptions: {
            namespace,
            objectType,
          },
        }),
      ),
      map((response) => (response?.pipelines ? this.convertPipelines(response.pipelines) : [])),
    );
  }

  getPipeline$(pipelineId: string): Observable<Pipeline> {
    return this.getMultiPipeline$([pipelineId]).pipe(map((pipelines) => pipelines[0]));
  }

  getMultiPipeline$(pipelineIds: string[]): Observable<Pipeline[]> {
    return this.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.pipelineService.getMultiPipeline({
          namespace,
          pipelineIds,
        }),
      ),
      map((response) => (response?.pipelines ? this.convertPipelines(response.pipelines) : [])),
      shareReplay(1),
    );
  }

  updatePipeline$(objectType: string, pipeline: Pipeline): Observable<HttpResponse<null>> {
    return this.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return this.pipelineService.updatePipeline({
          pipeline: {
            namespace: namespace,
            objectType: objectType,
            pipelineId: pipeline.id,
            name: pipeline.name,
            stages: pipeline.stages.map((stage) => ({
              stageId: stage.id || '',
              name: stage.name,
              filterGroup: convertGalaxyFilters(stage.filters),
              mutations: stage.mutations,
            })),
          },
          fieldMask: this.buildFieldMask(pipeline),
        });
      }),
    );
  }

  deletePipeline$(pipelineId: string): Observable<HttpResponse<null>> {
    return this.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return this.pipelineService.deletePipeline({
          namespace,
          pipelineId,
        });
      }),
    );
  }

  createPipeline$(objectType: string, pipeline: Pipeline): Observable<string> {
    return this.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        return this.pipelineService.createPipeline({
          pipeline: {
            namespace,
            objectType,
            name: pipeline.name,
            stages: pipeline.stages.map((stage) => ({
              name: stage.name,
              filterGroup: convertGalaxyFilters(stage.filters),
              mutations: stage.mutations,
            })),
          },
        });
      }),
      map((response) => response?.pipelineId || ''),
    );
  }

  private convertPipelines(pipelines: ProtoPipeline[]) {
    return pipelines.map((pipeline) => ({
      id: pipeline.pipelineId,
      name: pipeline.name,
      stages: pipeline.stages
        ?.map(
          (stage) =>
            ({
              id: stage.stageId,
              name: stage.name,
              filters: stage.filterGroup.filters.map((filter) => convertToGalaxyFilter(filter)),
              mutations: stage.mutations,
            }) as Stage,
        )
        .sort((a, b) => compareByProbability(a, b)),
    }));
  }

  private buildFieldMask(pipeline: Pipeline) {
    const paths = [];
    if (pipeline.name) {
      paths.push('name');
    }
    if (pipeline.stages) {
      paths.push('stages');
    }

    return new FieldMask({ paths });
  }
}
